import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/data-display/tooltip";
import { Skeleton } from "@/components/ui/feedback/skeleton";
import { RationaleTooltipContent } from "@/components/ui/molecules/feed-article-tooltips/RationaleTooltip";
import { SocialEngagementTooltipContent } from "@/components/ui/molecules/feed-article-tooltips/SocialEngagementTooltip";
import { cn } from "@/lib/utils";
import { abbreviateNumber, addCommasToNumbers } from "@/lib/utils/prettyName";
import { useDiscoveryContext } from "@/store/discovery/discovery-params.context";
import { useFeedName } from "@/store/news-feeds/news-feeds.selectors";
import {
  SmileyIcon,
  SmileyMehIcon,
  SmileySadIcon,
} from "@phosphor-icons/react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import type { ICellRendererParams } from "ag-grid-community";
import { type ReactNode, memo, useMemo } from "react";
import type { FeedArticleRowData } from "./FeedArticlesTableStates";

type NumberFormat = "abbreviate" | "commas" | "none";

interface FeedArticleCellRendererProps {
  isFetchingArticles?: boolean;
  params: ICellRendererParams<FeedArticleRowData>;
  prefixRenderer?: ReactNode;
  suffixRenderer?: ReactNode;
  valueClassName?: string;
  wrapText?: boolean;
  numberFormat?: NumberFormat;
  isLink?: boolean;
  url?: string | ((data: FeedArticleRowData) => string | undefined);
  showSentimentBadge?: boolean;
  showSocialEngagementTooltip?: boolean;
}

const FeedArticleCellRenderer = ({
  isFetchingArticles,
  params,
  prefixRenderer,
  suffixRenderer,
  valueClassName,
  wrapText = false,
  numberFormat = "none",
  isLink = false,
  url,
  showSentimentBadge = false,
  showSocialEngagementTooltip = false,
}: FeedArticleCellRendererProps) => {
  const { value, data } = params;
  const { feedId } = useDiscoveryContext();
  const feedName = useFeedName(feedId);

  const sentimentBadge = useMemo(() => {
    if (!showSentimentBadge || typeof value !== "string") return null;

    const sentimentCategory =
      value.toLowerCase() === "positive"
        ? "Positive"
        : value.toLowerCase() === "negative"
          ? "Negative"
          : value.toLowerCase() === "neutral"
            ? "Neutral"
            : null;

    if (!sentimentCategory) return null;

    return (
      <span
        className={cn(
          "px-2.5 py-1 rounded-md font-medium overflow-hidden text-ellipsis whitespace-nowrap",
          sentimentCategory === "Positive" && "text-green-700 bg-green-50",
          sentimentCategory === "Negative" && "text-red-700 bg-red-50",
          sentimentCategory === "Neutral" && "text-gray-600 bg-gray-50",
        )}
      >
        {sentimentCategory}
      </span>
    );
  }, [showSentimentBadge, value]);

  const formattedValue = useMemo(() => {
    if (showSentimentBadge && sentimentBadge) return "";
    if (value == null) return "-";
    if (typeof value !== "number") return value;

    switch (numberFormat) {
      case "abbreviate":
        return abbreviateNumber(value);
      case "commas":
        return addCommasToNumbers(value);
      default:
        return value;
    }
  }, [value, numberFormat, showSentimentBadge, sentimentBadge]);

  // Show skeleton if fetching or if this is a placeholder row (no data yet)
  if (isFetchingArticles || !data) {
    return <Skeleton className="h-5 w-full align-middle mt-1.5" />;
  }

  const contentClassName = cn(
    valueClassName,
    wrapText
      ? "wrap-break-word leading-relaxed"
      : "overflow-hidden text-ellipsis whitespace-nowrap",
    isLink && "cursor-pointer",
  );

  const handleClick = () => {
    if (!isLink || !data) return;

    const articleUrl = typeof url === "function" ? url(data) : url;

    if (articleUrl) {
      window.open(articleUrl, "_blank", "noopener,noreferrer");
    } else {
      console.log("No URL provided");
    }
  };

  // Check if this is the social engagements column
  const socialMetrics = data?.socialMetrics ?? null;

  // Show tooltip only if prop is true AND we have a social engagement value (not null)
  // The value represents the total social engagement displayed in the cell
  const shouldShowTooltip = showSocialEngagementTooltip && value !== null;

  // Check if we should show rationale tooltip for sentiment
  const sentimentRationale = data?.sentimentRationale ?? null;
  const shouldShowRationaleTooltip =
    showSentimentBadge && sentimentBadge && sentimentRationale !== null;

  // Get sentiment icon for tooltip
  const getSentimentIcon = (sentimentValue: string | null | undefined) => {
    if (!sentimentValue) return null;
    const polarity = sentimentValue.toLowerCase();
    switch (polarity) {
      case "positive":
        return (
          <span className="text-green-800">
            <SmileyIcon size={16} />
          </span>
        );
      case "negative":
        return (
          <span className="text-rose-700/90">
            <SmileySadIcon size={16} />
          </span>
        );
      default:
        return (
          <span>
            <SmileyMehIcon size={16} />
          </span>
        );
    }
  };

  const numberDisplay = (
    <span
      className={cn(contentClassName, shouldShowTooltip && "cursor-pointer")}
      onClick={isLink ? handleClick : undefined}
      onKeyDown={(e) => {
        if (isLink && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          handleClick();
        }
      }}
      role={isLink ? "button" : undefined}
      tabIndex={isLink ? 0 : undefined}
    >
      {formattedValue}
    </span>
  );

  return (
    <div
      className={cn(
        "flex gap-1 min-w-0",
        wrapText ? "items-start" : "items-center",
      )}
    >
      {prefixRenderer && <div className="shrink-0">{prefixRenderer}</div>}
      {sentimentBadge ? (
        shouldShowRationaleTooltip ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="shrink-0 cursor-pointer">{sentimentBadge}</div>
              </TooltipTrigger>
              <TooltipPrimitive.Portal>
                <TooltipContent className="px-2 py-4 max-w-md">
                  <RationaleTooltipContent
                    rationale={sentimentRationale}
                    title={`Why is this article ${value?.toLowerCase() ?? ""} for ${feedName}?`}
                    icon={getSentimentIcon(value)}
                  />
                </TooltipContent>
              </TooltipPrimitive.Portal>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <div className="shrink-0">{sentimentBadge}</div>
        )
      ) : shouldShowTooltip ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>{numberDisplay}</TooltipTrigger>
            <TooltipPrimitive.Portal>
              <TooltipContent className="p-4 w-64 bg-white shadow-md rounded-lg z-50 relative overflow-visible">
                <SocialEngagementTooltipContent
                  socialMetrics={socialMetrics}
                  loading={false}
                />
              </TooltipContent>
            </TooltipPrimitive.Portal>
          </Tooltip>
        </TooltipProvider>
      ) : (
        numberDisplay
      )}
      {suffixRenderer && <div className="shrink-0">{suffixRenderer}</div>}
    </div>
  );
};

FeedArticleCellRenderer.displayName = "FeedArticleCellRenderer";

export default memo(FeedArticleCellRenderer);
